import React, { useState } from "react";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { useReport } from "../data";
import { downloadAdditionalFile } from "../../../api/routes";
import styles from "./DocumentsTab.module.css";

const save = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

interface DocumentsTabProps {
  applicationId: string;
  reportId: string;
}

// Documents generated from this application, stored as report attachments and
// linked back here via their policy_application_id.
export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  applicationId,
  reportId,
}) => {
  const { data: report, isLoading } = useReport(reportId);
  const [downloading, setDownloading] = useState<string | null>(null);

  if (isLoading || !report) {
    return (
      <div className={styles.loading}>
        <ProgressSpinner style={{ width: 44, height: 44 }} />
      </div>
    );
  }

  const documents = (
    report.report_json.attachment_info?.attachments ?? []
  ).filter((attachment) => attachment.policy_application_id === applicationId);

  const downloadDocument = async (name: string) => {
    setDownloading(name);
    try {
      save(await downloadAdditionalFile(reportId, name), name);
    } finally {
      setDownloading(null);
    }
  };

  if (documents.length === 0) {
    return (
      <div className={styles.placeholder}>
        <i className="pi pi-folder-open" />
        <h3>No documents yet</h3>
        <p>
          Documents generated from this application (quotes, binders, policies)
          appear here. Use “Generate &amp; Save Quote” on the Quote tab to add
          one.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.inner}>
      <h2 className={styles.heading}>Generated Documents</h2>
      <div className={styles.list}>
        {documents.map((attachment) => (
          <div key={attachment.name} className={styles.row}>
            <i className="pi pi-file-pdf" />
            <span className={styles.name}>{attachment.name}</span>
            <Button
              icon="pi pi-download"
              rounded
              text
              size="small"
              tooltip="Download"
              loading={downloading === attachment.name}
              onClick={() => void downloadDocument(attachment.name)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
